import { daysUntil } from '../audit/windows.js';

/**
 * Task rules shared by the app and the server (api/src/writePolicy.ts) —
 * owner rulings 2026-10-25 (docs/beta-features.md B1 + B3). Pure; keep it
 * free of browser and Graph imports (the API compiles it too).
 */

/**
 * B3 — a store may undo its own completion until the END OF THE NEXT DAY,
 * in Eastern calendar days (the app's and flows' day — windows.ts): done
 * Tuesday 9pm → undo until Wednesday 11:59pm Eastern.
 */
export function withinUndoWindow(completedAt: string | null | undefined, now: Date): boolean {
  if (!completedAt) return false;
  const daysSince = -daysUntil(completedAt, now);
  return Number.isFinite(daysSince) && daysSince >= 0 && daysSince <= 1;
}

const same = (a: unknown, b: string) =>
  typeof a === 'string' && a.trim() !== '' && a.trim().toLowerCase() === b.trim().toLowerCase();

/**
 * Which completions a store may undo (owner ruling 2026-10-25): only a task
 * assigned to the store's own account that the store account itself
 * completed, inside the window. Tasks assigned to a person stay DM-reopen.
 */
export function canUndoCompletion(
  task: { status: string; assignedTo: string; completedByUpn: string; completedAt: string | null },
  user: { upn: string; role: string },
  now: Date,
): boolean {
  return (
    user.role === 'StoreTeam' &&
    task.status === 'complete' &&
    same(task.assignedTo, user.upn) &&
    same(task.completedByUpn, user.upn) &&
    withinUndoWindow(task.completedAt, now)
  );
}

/**
 * B1 — "Proof required": a photo or PDF must be on the task, uploaded since
 * the task was created or last reopened (ProofSince). Two minutes of slack
 * for clock skew between the API and SharePoint. A blank ProofSince (older
 * task) = any file counts.
 */
export const PROOF_SKEW_MS = 2 * 60_000;

export function hasProofSince(fileTimes: readonly string[], proofSince: string | null | undefined): boolean {
  if (!proofSince) return fileTimes.length > 0;
  const since = Date.parse(proofSince);
  if (!Number.isFinite(since)) return fileTimes.length > 0;
  return fileTimes.some((t) => Date.parse(t) >= since - PROOF_SKEW_MS);
}

export const PROOF_REQUIRED_MESSAGE = 'Add a photo or PDF to this task before completing it.';

/**
 * B2 — "Ask for more time" (owner rulings 2026-10-25). The store asks for a
 * new due date with a reason; the due date only moves when a DM, Auditor or
 * Admin accepts (lateness keeps counting until then).
 */
export const MAX_EXTENSION_DAYS = 30;
export const EXTENSION_REASON_MAX = 500;

export interface ExtensionRequest {
  /** The account that asked (server-stamped). */
  by: string;
  /** The person's typed name behind a shared store account. */
  name: string;
  /** When it was asked (server-stamped ISO). */
  at: string;
  /** The asked-for due date, YYYY-MM-DD. */
  date: string;
  reason: string;
}

const DAY = 86_400_000;

const EASTERN = new Intl.DateTimeFormat('en-CA', { timeZone: 'America/New_York', year: 'numeric', month: '2-digit', day: '2-digit' });
/** Today in Eastern calendar days as YYYY-MM-DD — the day the rules count in (date pickers use it too). */
export function easternIsoDay(now: Date): string {
  return EASTERN.format(now);
}
const isoDay = (d: string) => /^\d{4}-\d{2}-\d{2}$/.test(d) && Number.isFinite(Date.parse(`${d}T00:00:00Z`));

/**
 * Why an asked-for date isn't allowed, or null when it is: after today
 * (Eastern) and after the current due date, at most 30 days past the
 * LATER of the current due date and today.
 */
export function extensionDateProblem(date: string, dueDate: string | null | undefined, now: Date): string | null {
  if (!isoDay(date)) return 'Pick a new due date.';
  if (daysUntil(date, now) < 1) return 'The new date must be after today.';
  const due = (dueDate ?? '').slice(0, 10);
  if (isoDay(due) && date <= due) return 'The new date must be after the current due date.';
  const asked = Date.parse(`${date}T00:00:00Z`);
  // Counted from the later of the current due date and today (Eastern days).
  const today = asked - daysUntil(date, now) * DAY;
  const base = isoDay(due) ? Math.max(Date.parse(`${due}T00:00:00Z`), today) : today;
  if ((asked - base) / DAY > MAX_EXTENSION_DAYS) {
    return `Ask for at most ${MAX_EXTENSION_DAYS} days more.`;
  }
  return null;
}

/** The asked-for date is already behind us (Eastern) — accepting it would set a past due date. */
export function extensionDatePassed(date: string, now: Date | number): boolean {
  return daysUntil(date, typeof now === 'number' ? new Date(now) : now) < 0;
}

/** The pending request on a task row, or null (blank, unreadable, or an old shape). */
export function parseExtensionRequest(raw: unknown): ExtensionRequest | null {
  if (typeof raw !== 'string' || raw.trim() === '') return null;
  try {
    const v = JSON.parse(raw) as Partial<ExtensionRequest> & { v?: number };
    if (v.v !== 1 || typeof v.date !== 'string' || !isoDay(v.date) || typeof v.reason !== 'string') return null;
    return {
      by: typeof v.by === 'string' ? v.by : '',
      name: typeof v.name === 'string' ? v.name : '',
      at: typeof v.at === 'string' ? v.at : '',
      date: v.date,
      reason: v.reason.slice(0, EXTENSION_REASON_MAX),
    };
  } catch {
    return null;
  }
}

/** A task's status as people read it (UI review U45/U186: never the stored "complete"/"open"). */
export function taskStatusLabel(status: string | null | undefined): string {
  if (status === 'complete') return 'Completed';
  if (status === 'reopened') return 'Reopened';
  return 'Open';
}
